<template>
  <span class="kds-badge" :class="`kds-badge--${resolvedTone}`">
    <slot />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { KdsBadgeTone } from '@/kds/types';

interface Props {
  tone?: KdsBadgeTone;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'muted'
});

const KNOWN: KdsBadgeTone[] = ['ok', 'warn', 'info', 'muted', 'live', 'danger'];

// Fallback safety (edge case: unknown tone → muted).
const resolvedTone = computed<KdsBadgeTone>(() =>
  KNOWN.includes(props.tone) ? props.tone : 'muted'
);
</script>

<style scoped lang="scss">
.kds-badge {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 10.5px;
  line-height: 1;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: var(--r-1);
  border: var(--bw) solid transparent;

  &--ok {
    background: var(--ok);
    color: var(--paper);
  }

  &--warn {
    background: var(--warn);
    color: var(--ink);
  }

  &--info {
    background: var(--info);
    color: var(--paper);
  }

  &--muted {
    background: var(--paper-2);
    color: var(--stone);
    border-color: var(--mist);
  }

  &--live {
    background: var(--signal-tint);
    color: var(--ink);
    border-color: var(--signal-press);
  }

  &--danger {
    background: color-mix(in srgb, var(--danger) 12%, transparent);
    color: var(--danger);
    border-color: color-mix(in srgb, var(--danger) 24%, transparent);
  }
}
</style>
